/**
 * Generic range-status classifier for calculator quizzes.
 *
 * A `Range` is an inclusive low + inclusive high. Values strictly below `low`
 * map to "low"; values strictly above `high` map to "high"; everything in
 * `[low, high]` maps to "normal".
 *
 * Reference ranges for free testosterone derive from Jasuja et al. 2023
 * (Andrology), broader adult male band. Bioavailable T and percent free use
 * the commonly cited literature ranges from the source calculator.
 */

export type RangeStatus = "low" | "normal" | "high";

export interface Range {
  low: number;
  high: number;
}

export function classifyByRange(value: number, range: Range): RangeStatus {
  if (!Number.isFinite(value)) {
    throw new Error(`value must be finite; got ${value}`);
  }
  if (!(range.low < range.high)) {
    throw new Error(
      `range.low must be strictly less than range.high; got low=${range.low} high=${range.high}`,
    );
  }
  if (value < range.low) return "low";
  if (value > range.high) return "high";
  return "normal";
}

export const FT_RANGE_PGML: Range = { low: 66, high: 309 };
export const BIO_T_RANGE_NGDL: Range = { low: 131, high: 682 };
export const PCT_FREE_RANGE: Range = { low: 2.0, high: 3.0 };

export function freeTStatusPgml(value: number): RangeStatus {
  return classifyByRange(value, FT_RANGE_PGML);
}

export function bioTStatusNgdl(value: number): RangeStatus {
  return classifyByRange(value, BIO_T_RANGE_NGDL);
}

export function pctFreeStatus(value: number): RangeStatus {
  return classifyByRange(value, PCT_FREE_RANGE);
}
